import { fromJS } from 'immutable'
import { json, RxConversationAPI, checkStatus, ConversationAPI, CustomerAPI, RxCustomerAPI } from '@driftt/dapper'
import { Observable } from 'rxjs/Observable'
import { encodeQueryData } from 'utils/Helpers'

import { playbookListNormalizer, playbookNormalizer } from './schema'
import { PlaybookStatus, chatPlaybookReportTypes } from './constants'
import { weakUuid } from './helpers'
import * as FileAPI from 'app/api/FileAPI'

const parsePlaybooks = res =>
  playbookListNormalizer(
    res.map(pb => {
      const targetingDisplay = _.get(pb, 'targeting.display')
      if (!!targetingDisplay) {
        pb.targeting.display = pb.targeting.display.map(conditionSet => ({
          conditions: conditionSet.map(condition => ({ id: weakUuid(), ...condition })),
          id: weakUuid(),
        }))
      }
      return pb
    })
  )
const parsePlaybook = res => {
  // TODO: get rid of this code once the backend returns signatures with an "id" property
  const isSignature = res.reportType === 'SIGNATURE'
  let newActions = res.actions
  if (isSignature) {
    newActions = [
      {
        id: res.actions[0].id,
        actionType: res.actions[0].actionType,
        action: {
          signatureActions: res.actions[0].action.signatureActions.map(sig => _.assign(sig, { id: sig.signatureId }))
        }
      }
    ]
  }
  const targetingDisplay = _.get(res, 'targeting.display')
  if (!!targetingDisplay) {
    const newTargeting = _.assign(res.targeting, { display: targetingDisplay.map(conditionSet => ({
      conditions: conditionSet.map(condition => ({ id: weakUuid(), ...condition })),
      id: weakUuid(),
    })) })
    return playbookNormalizer(_.assign(res, { targeting: newTargeting }, { actions: newActions }))
  } else {
    return playbookNormalizer(_.assign(res, { actions: newActions }))
  }
}

export const api = {
  fetchChatActive() {
    return RxConversationAPI.get(`/playbooks?${encodeQueryData({ status: ['ENABLED', 'DISABLED'], reportType: chatPlaybookReportTypes, limited: true })}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parsePlaybooks)
      .map(fromJS)
  },
  search(status, parse = true) {
    return RxConversationAPI.get(`/playbooks${!!status ? `?${encodeQueryData({ status })}` : ''}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(response => (parse ? parsePlaybooks(response) : response))
      .map(fromJS)
  },
  // deprecate & refactor this in favor of search w/status arg ^^
  fetchEnabled() {
    return RxConversationAPI.get(`/playbooks?status=${PlaybookStatus.ENABLED}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parsePlaybooks)
      .map(fromJS)
  },
  fetchCardinality({ orgId, userListIds, excludedUserListIds, smartFilters, isEmailPlaybook }) {
    return RxCustomerAPI.post(
      `/user_lists/count/inc_exc`,
      {
        inclusionLists: userListIds.toJS(),
        exclusionLists: excludedUserListIds.toJS(),
        smartFilters,
        orgId,
        isEmailCampaign: isEmailPlaybook
      }
    )
      .checkStatus()
      .retryWithBackoff()
  },
  counts() {
    return RxConversationAPI.get(`/playbooks/counts`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(fromJS)
  },
  enroll(playbookId, endUserId, action) {
    return RxConversationAPI.post(`/playbooks/${playbookId}/manual_enroll`, { endUserId, action })
  },
  unenroll(endUserId) {
    return RxConversationAPI.post(`/playbooks/unenroll`, { endUserId })
      .checkStatus()
      .retryWithBackoff()
  },
  fetchEnrolledPlaybookId(email) {
    return RxConversationAPI.get(`/sequences/for/${email}`)
  },
  fetchPlaybookByCampaignIdUntouched(id) {
    return RxConversationAPI.get(`/campaigns/${id}/playbook`)
      .checkStatus()
      .retryWithBackoff()
      .map(({ response }) => response)
  },
  fetchPlaybookByCampaignId(id) {
    return RxConversationAPI.get(`/campaigns/${id}/playbook`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(res => parsePlaybooks([res]))
      .map(fromJS)
  },
  fetchPlaybookByInteractionId(id) {
    return RxConversationAPI.get(`/playbooks/by_interaction_id/${id}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(res => parsePlaybooks([res]))
      .map(fromJS)
  },
  fetch(id) {
    return RxConversationAPI.get(`/playbooks/${id}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parsePlaybook)
      .map(fromJS)
  },
  fetchPersonalized(id, endUserId) {
    return ConversationAPI.get(`/playbooks/${id}`)
      .then(checkStatus)
      .then(json)
      .then(fromJS)
  },
  update(playbook) {
    return RxConversationAPI.patch(`/playbooks`, playbook)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parsePlaybook)
      .map(fromJS)
  },
  updateStatus(id, status) {
    return RxConversationAPI.patch(`/playbooks/${id}/${status}`)
      .checkStatus()
      .retryWithBackoff(undefined, undefined, [400, 403, 404])
      .mapResponse(parsePlaybook)
      .map(fromJS)
  },
  create(type) {
    return RxConversationAPI.post(`/playbooks?${encodeQueryData({ type })}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parsePlaybook)
      .map(fromJS)
  },
  clone(id) {
    return RxConversationAPI.post(`/playbooks/${id}/clone`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parsePlaybook)
      .map(fromJS)
  },
  reorder(orderedPlaybookIds) {
    return RxConversationAPI.patch(`/playbooks/reorder`, orderedPlaybookIds)
      .checkStatus()
      .retryWithBackoff()
  },
  byIds(playbookIds) {
    return RxConversationAPI.post(`/playbooks/by_id`, playbookIds)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(response => response)
  },
  // wrapping in RX here b/c Targeting API is SUPER OLD
  fetchTarget(id) {
    return RxConversationAPI.get(`/targets/${id}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse()
  },
  sendTestEmail(id, actionId, email, emailId, playbook) {
    return RxConversationAPI.post(`/playbooks/${id}/test_email/${actionId}/v2`, { emails: [email], emailId, playbookEdit: playbook })
      .checkStatus()
      .retryWithBackoff()
  },
  sendTestSequence(playbookId, actionId, email, playbook) {
    return RxConversationAPI.post(`/playbooks/${playbookId}/test_email_sequence/${actionId}/v2`, { emails: [email], playbookEdit: playbook, delayInSeconds: 240 })
      .checkStatus()
      .retryWithBackoff()
  },
  fetchDataPreviewEmail(templateBody, userListIds = [], campaignId, userId) {
    return RxConversationAPI.post(`/playbooks/template_preview`, { templateBody, userListIds, campaignId, userId })
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },
  notify(id, userIds) {
    return RxConversationAPI.post(`/playbooks/${id}/notify`, { userIds })
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },
  fetchEnrolledSequenceCount() {
    return RxConversationAPI.get(`/sequences/sends`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(fromJS)
  },
  fetchPlaybookForEndUser(options) {
    return RxConversationAPI.get(
      `/playbooks/${options.playbookId}/for_end_user?${encodeQueryData({ endUserId: options.email })}`
    )
      .retryWithBackoff()
      .mapResponse(fromJS)
  },
  uploadImage(file) {
    return Observable.fromPromise(FileAPI.upload(file))
      .retryWithBackoff()
  },
  fetchPlaybooksByGatedPlan() {
    return RxConversationAPI.get('/playbooks/playbooks_by_gate')
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },
  fetchSignatureMultiBannerPreviewForPlaybook({ userId, playbook, isPlaybookEditor }) {
    return RxConversationAPI.post(`/playbooks/signature_preview/${userId}/v2${isPlaybookEditor ? '?isPlaybookEditor=true' : ''}`, playbook)
      .checkStatus()
      .map(res => res.response)
  },
  createInitialWelcomePlaybooks({ onlineMsg, offlineMsg }) {
    return RxConversationAPI.post(`/playbooks/welcome_messages`, { onlineText: onlineMsg, offlineText: offlineMsg })
      .checkStatus()
      .retryWithBackoff(2)
  },
  fetchPlaybooksByReportType({ reportType }) {
    return RxConversationAPI.get(`/playbooks?${encodeQueryData({ limited: true, reportType })}`)
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },
  enableAllPlaybooksByReportType({ orgId, reportType }) {
    return RxConversationAPI.post(`/playbooks/${orgId}/enable_playbooks`, reportType)
      .checkStatus()
      .retryWithBackoff()
      .map(res => res.response)
  },
  disableSalesSequenceById(playbookId) {
    return CustomerAPI.post(`/sequences/bulk_unenroll/${playbookId}`).then(json)
  }
}



// WEBPACK FOOTER //
// ./src/scripts/app/modules/playbook/api.js